import { useState, useEffect } from 'react'
import { useSearchParams } from 'react-router-dom'
import { toast } from 'react-hot-toast'
import { Plus, Pencil, Trash2, Filter, Search, ChevronDown, ChevronUp, Loader2, Tag, Zap, Palette, Calendar, FileText } from 'lucide-react'
import {
  Card,
  CardContent,
  Button,
  Input,
  Label,
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Badge,
  Textarea,
  Separator,
} from '@jira-clone/ui'
import { useDeclarations, useDeclarationMutations } from '../../shared/hooks/useDeclarations'
import { getApiErrorMessage } from '../../shared/utils/apiError'

interface DraftState {
  id?: string
  title: string
  content: string
  isDefault: boolean
  isActive: boolean
}

const EMPTY_DRAFT: DraftState = {
  title: '',
  content: '',
  isDefault: false,
  isActive: true,
}

export default function DeclarationAdminPage() {
  const [searchParams, setSearchParams] = useSearchParams()
  const [search, setSearch] = useState(searchParams.get('search') || '')
  const [isActiveFilter, setIsActiveFilter] = useState<'all' | 'true' | 'false'>(searchParams.get('isActive') as any || 'all')

  const handleSearchChange = (value: string) => {
    setSearch(value)
    setSearchParams(value ? { search: value } : {})
  }

  const handleIsActiveChange = (value: 'all' | 'true' | 'false') => {
    setIsActiveFilter(value)
    setSearchParams(value === 'all' ? {} : { isActive: value })
  }

  const { data: declarations = [], isLoading } = useDeclarations({
    isActive: isActiveFilter === 'all' ? undefined : isActiveFilter === 'true',
    search: search || undefined,
  })
  
  const { create, update, setStatus, remove, setDefault } = useDeclarationMutations()

  const [dialogOpen, setDialogOpen] = useState(false)
  const [draft, setDraft] = useState<DraftState>(EMPTY_DRAFT)
  const [deletingId, setDeletingId] = useState<string | null>(null)

  const openCreate = () => {
    setDraft(EMPTY_DRAFT)
    setDialogOpen(true)
  }

  const openEdit = (declaration: any) => {
    setDraft({
      id: declaration.id,
      title: declaration.title,
      content: declaration.content || '',
      isDefault: declaration.isDefault,
      isActive: declaration.isActive,
    })
    setDialogOpen(true)
  }

  const handleSave = async () => {
    if (!draft.title.trim()) {
      toast.error('Title is required.')
      return
    }
    if (!draft.content.trim()) {
      toast.error('Content is required.')
      return
    }
    try {
      const payload = {
        title: draft.title.trim(),
        content: draft.content.trim(),
        isDefault: draft.isDefault,
        isActive: draft.isActive,
      }
      if (draft.id) {
        await update.mutateAsync({ id: draft.id, payload })
        toast.success('Declaration updated.')
      } else {
        await create.mutateAsync(payload)
        toast.success('Declaration created.')
      }
      setDialogOpen(false)
    } catch (err) {
      toast.error(getApiErrorMessage(err, draft.id ? 'update this declaration' : 'create this declaration'))
    }
  }

  const handleDelete = async (declaration: any) => {
    if (!confirm(`Delete "${declaration.title}"? This can't be undone.`)) return
    setDeletingId(declaration.id)
    try {
      await remove.mutateAsync(declaration.id)
      toast.success('Declaration deleted.')
    } catch (err) {
      toast.error(getApiErrorMessage(err, 'delete this declaration'))
    } finally {
      setDeletingId(null)
    }
  }

  const handleToggleActive = async (declaration: any) => {
    try {
      await setStatus.mutateAsync({ id: declaration.id, isActive: !declaration.isActive })
      toast.success(declaration.isActive ? 'Declaration deactivated.' : 'Declaration activated.')
    } catch (err) {
      toast.error(getApiErrorMessage(err, 'toggle declaration status'))
    }
  }

  const handleSetDefault = async (declaration: any) => {
    try {
      await setDefault.mutateAsync(declaration.id)
      toast.success('Default declaration updated.')
    } catch (err) {
      toast.error(getApiErrorMessage(err, 'set as default'))
    }
  }

  return (
    <div className="space-y-4 max-w-4xl">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-bold text-foreground">Declarations</h2>
          <p className="text-sm text-muted-foreground">Manage standard declarations for quotations and invoices.</p>
        </div>
        <Button onClick={openCreate} className="gap-1.5">
          <Plus className="h-4 w-4" /> New Declaration
        </Button>
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="p-4">
          <div className="flex flex-wrap items-center gap-4">
            {/* Search */}
            <div className="relative flex-1 min-w-[200px]">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search declarations..."
                value={search}
                onChange={(e) => handleSearchChange(e.target.value)}
                className="pl-9"
              />
            </div>

            {/* Active Filter */}
            <Select value={isActiveFilter} onValueChange={handleIsActiveChange}>
              <SelectTrigger className="w-[140px]">
                <SelectValue placeholder="All Status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All</SelectItem>
                <SelectItem value="true">Active</SelectItem>
                <SelectItem value="false">Inactive</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {isLoading ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
        </div>
      ) : declarations.length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center">
            <p className="text-sm text-muted-foreground">
              No declarations yet. Create the first one.
            </p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-2">
          {declarations.map((declaration) => (
            <Card key={declaration.id}>
              <CardContent className="p-4">
                <div className="flex items-center justify-between gap-3">
                  <div className="flex items-center gap-3 min-w-0 flex-1">
                    <div className="flex items-center gap-2">
                      <FileText className="h-5 w-5 text-muted-foreground" />
                      <Badge variant={declaration.isActive ? 'default' : 'secondary'} className="text-xs">
                        {declaration.isActive ? 'Active' : 'Inactive'}
                      </Badge>
                      {declaration.isDefault && (
                        <Badge variant="outline" className="text-xs bg-primary/10 text-primary border-primary">
                          Default
                        </Badge>
                      )}
                    </div>
                    <div className="min-w-0">
                      <div className="flex items-center gap-2">
                        <p className="text-sm font-semibold text-foreground truncate">{declaration.title}</p>
                      </div>
                      <p className="text-xs text-muted-foreground truncate line-clamp-2">
                        {declaration.content}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-1 shrink-0">
                    <Button variant="ghost" size="icon" onClick={() => openEdit(declaration)} title="Edit">
                      <Pencil className="h-3.5 w-3.5" />
                    </Button>
                    {declaration.isDefault ? (
                      <Badge variant="outline" className="text-xs bg-primary/10 text-primary border-primary">
                        Default
                      </Badge>
                    ) : (
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => handleSetDefault(declaration)}
                        title="Set as Default"
                      >
                        <Calendar className="h-3.5 w-3.5 text-primary" />
                      </Button>
                    )}
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => handleToggleActive(declaration)}
                      title={declaration.isActive ? 'Deactivate' : 'Activate'}
                    >
                      {declaration.isActive ? (
                        <span className="h-3.5 w-3.5 rounded-full bg-green-500" />
                      ) : (
                        <span className="h-3.5 w-3.5 rounded-full bg-gray-400" />
                      )}
                    </Button>
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => handleDelete(declaration)}
                      disabled={deletingId === declaration.id}
                      title="Delete"
                    >
                      <Trash2 className="h-3.5 w-3.5 text-destructive" />
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="sm:max-w-2xl">
          <DialogHeader>
            <DialogTitle>{draft.id ? 'Edit Declaration' : 'New Declaration'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-2 max-h-[70vh] overflow-y-auto">
            <div className="space-y-1.5">
              <Label htmlFor="dec-title">Title *</Label>
              <Input
                id="dec-title"
                value={draft.title}
                onChange={(e) => setDraft((d) => ({ ...d, title: e.target.value }))}
                placeholder="e.g. Standard Declaration"
              />
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="dec-content">Content *</Label>
              <Textarea
                id="dec-content"
                value={draft.content}
                onChange={(e) => setDraft((d) => ({ ...d, content: e.target.value }))}
                placeholder="Full declaration text"
                rows={5}
              />
            </div>

            <Separator className="my-2" />

            <div className="flex items-center gap-2">
              <Label className="flex items-center gap-2 text-sm cursor-pointer">
                <input
                  id="dec-default"
                  type="checkbox"
                  checked={draft.isDefault}
                  onChange={(e) => setDraft((d) => ({ ...d, isDefault: e.target.checked }))}
                />
                <label htmlFor="dec-default" className="cursor-pointer">Set as default declaration</label>
              </Label>
            </div>

            <div className="flex items-center gap-2">
              <Label className="flex items-center gap-2 text-sm cursor-pointer">
                <input
                  type="checkbox"
                  checked={draft.isActive}
                  onChange={(e) => setDraft((d) => ({ ...d, isActive: e.target.checked }))}
                />
                <span>Active</span>
              </Label>
            </div>
          </div>
          <DialogFooter className="mt-4">
            <Button variant="outline" onClick={() => setDialogOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handleSave} disabled={create.isPending || update.isPending}>
              {draft.id ? 'Save Changes' : 'Create Declaration'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  )
}